/**
 * Button templates & per-device buttons (brief §4, ADR 0011).
 *
 * The template library (fleet layer, versioned per vendor/model), template
 * assignment per device, and the device-layer editor: `device` rows override
 * a key, `phone_local` rows leave the key to the phone (omitted from rendered
 * config). The effective panel shows exactly what the renderer would emit.
 */

import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  assignButtonTemplate,
  createButtonTemplate,
  deleteButtonTemplate,
  getButtonTemplates,
  getDeviceButtons,
  getDevices,
  importVodiaButtons,
  setDeviceButtons,
  updateButtonTemplate,
} from "@pbxmgr/api";
import { ApiError } from "@pbxmgr/api";
import type {
  ButtonImportReport,
  ButtonTemplateRow,
  DeviceButtonLine,
  DeviceButtonRow,
  DeviceRow,
  TemplateKey,
} from "@pbxmgr/api";
import {
  Alert,
  Badge,
  Button,
  ConfirmButton,
  Spinner,
  TextField,
} from "@pbxmgr/ui";

import { usePermission } from "../auth/usePermission";
import { selectAll, selectionPayload, setAll, toggle } from "./importSelection";
import type { ImportSelection } from "./importSelection";
import { Panel } from "../dashboard/panels";

const LINE_ROLES = ["", "primary", "backup", "monitor"] as const;

function errorText(err: unknown): string {
  return err instanceof ApiError
    ? (err.detail ?? err.message)
    : "request failed";
}

/** An editable template key row (string-typed for form state). */
interface KeyDraft {
  key_index: string;
  key_type: string;
  target: string;
  label: string;
  line_role: string;
}

const EMPTY_KEY: KeyDraft = {
  key_index: "",
  key_type: "blf",
  target: "",
  label: "",
  line_role: "",
};

function toTemplateKeys(drafts: KeyDraft[]): TemplateKey[] {
  return drafts.map((d) => ({
    key_index: Number(d.key_index),
    key_type: d.key_type,
    target: d.target || null,
    label: d.label || null,
    line_role: (d.line_role || null) as TemplateKey["line_role"],
  }));
}

function toDrafts(keys: TemplateKey[]): KeyDraft[] {
  return keys.map((k) => ({
    key_index: String(k.key_index),
    key_type: k.key_type,
    target: k.target ?? "",
    label: k.label ?? "",
    line_role: k.line_role ?? "",
  }));
}

function KeyEditor({
  drafts,
  onChange,
}: {
  drafts: KeyDraft[];
  onChange: (next: KeyDraft[]) => void;
}): ReactNode {
  const set = (i: number, patch: Partial<KeyDraft>): void => {
    onChange(drafts.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  };
  return (
    <table className="ui-table">
      <thead>
        <tr>
          <th>Key #</th>
          <th>Type</th>
          <th>Target</th>
          <th>Label</th>
          <th>Line role</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {drafts.map((d, i) => (
          <tr key={i}>
            <td>
              <input
                aria-label={`key ${i} index`}
                value={d.key_index}
                size={4}
                onChange={(e) => set(i, { key_index: e.target.value })}
              />
            </td>
            <td>
              <input
                aria-label={`key ${i} type`}
                value={d.key_type}
                size={10}
                onChange={(e) => set(i, { key_type: e.target.value })}
              />
            </td>
            <td>
              <input
                aria-label={`key ${i} target`}
                value={d.target}
                size={12}
                onChange={(e) => set(i, { target: e.target.value })}
              />
            </td>
            <td>
              <input
                aria-label={`key ${i} label`}
                value={d.label}
                size={14}
                onChange={(e) => set(i, { label: e.target.value })}
              />
            </td>
            <td>
              <select
                aria-label={`key ${i} line role`}
                value={d.line_role}
                onChange={(e) => set(i, { line_role: e.target.value })}
              >
                {LINE_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r || "—"}
                  </option>
                ))}
              </select>
            </td>
            <td>
              <Button
                variant="secondary"
                onClick={() => onChange(drafts.filter((_, j) => j !== i))}
              >
                Remove
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TemplateLibrary(): ReactNode {
  const canManage = usePermission("buttons.templates.manage");
  const qc = useQueryClient();
  const templates = useQuery({
    queryKey: ["buttons", "templates"],
    queryFn: getButtonTemplates,
  });
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["buttons"] });
  };

  const [editing, setEditing] = useState<ButtonTemplateRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [vendor, setVendor] = useState("snom");
  const [model, setModel] = useState("");
  const [sidecars, setSidecars] = useState("0");
  const [description, setDescription] = useState("");
  const [drafts, setDrafts] = useState<KeyDraft[]>([]);
  const [error, setError] = useState<string | null>(null);

  const reset = (): void => {
    setEditing(null);
    setCreating(false);
    setError(null);
  };
  const startCreate = (): void => {
    reset();
    setCreating(true);
    setName("");
    setModel("");
    setSidecars("0");
    setDescription("");
    setDrafts([{ ...EMPTY_KEY, key_index: "0" }]);
  };
  const startEdit = (tpl: ButtonTemplateRow): void => {
    reset();
    setEditing(tpl);
    setSidecars(String(tpl.sidecar_count));
    setDescription(tpl.description ?? "");
    setDrafts(toDrafts(tpl.buttons));
  };

  const save = useMutation({
    mutationFn: () =>
      editing
        ? updateButtonTemplate(editing.id, {
            description: description || null,
            sidecar_count: Number(sidecars),
            buttons: toTemplateKeys(drafts),
          })
        : createButtonTemplate({
            name,
            vendor,
            model,
            sidecar_count: Number(sidecars),
            description: description || null,
            buttons: toTemplateKeys(drafts),
          }),
    onSuccess: () => {
      reset();
      invalidate();
    },
    onError: (err) => setError(errorText(err)),
  });
  const remove = useMutation({
    mutationFn: deleteButtonTemplate,
    onSuccess: invalidate,
    onError: (err) => setError(errorText(err)),
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    save.mutate();
  };
  const items = templates.data?.items ?? [];

  return (
    <Panel title={`Button templates (${items.length})`}>
      {templates.isLoading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <div className="ui-empty">
          No templates yet — a template is the fleet button layer.
        </div>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Model</th>
              <th>Sidecars</th>
              <th>Keys</th>
              <th>Version</th>
              {canManage ? <th></th> : null}
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td>
                  {t.vendor} {t.model}
                </td>
                <td>{t.sidecar_count}</td>
                <td>{t.buttons.length}</td>
                <td>v{t.version}</td>
                {canManage ? (
                  <td>
                    <div className="pairing-actions">
                      <Button variant="secondary" onClick={() => startEdit(t)}>
                        Edit
                      </Button>
                      <ConfirmButton
                        question={`Delete template ${t.name}?`}
                        disabled={remove.isPending}
                        onConfirm={() => remove.mutate(t.id)}
                      />
                    </div>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {canManage && !creating && !editing ? (
        <Button onClick={startCreate}>New template</Button>
      ) : null}

      {creating || editing ? (
        <form className="editor-form" onSubmit={submit}>
          <h3>
            {editing
              ? `Edit ${editing.name} (v${editing.version})`
              : "New template"}
          </h3>
          {editing ? null : (
            <>
              <TextField
                id="tpl-name"
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <TextField
                id="tpl-vendor"
                label="Vendor"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
              />
              <TextField
                id="tpl-model"
                label="Model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="D785"
              />
            </>
          )}
          <TextField
            id="tpl-sidecars"
            label="Sidecar count"
            value={sidecars}
            onChange={(e) => setSidecars(e.target.value)}
          />
          <TextField
            id="tpl-description"
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <KeyEditor drafts={drafts} onChange={setDrafts} />
          <div className="pairing-actions">
            <Button
              variant="secondary"
              onClick={() => setDrafts([...drafts, { ...EMPTY_KEY }])}
            >
              Add key
            </Button>
            <Button
              type="submit"
              disabled={save.isPending || (!editing && (!name || !model))}
            >
              {editing
                ? "Save (bumps version, re-provisions assigned devices)"
                : "Create"}
            </Button>
            <Button variant="secondary" onClick={reset}>
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
    </Panel>
  );
}

function AssignmentTable({
  devices,
  templates,
  onSelect,
  selected,
}: {
  devices: DeviceRow[];
  templates: ButtonTemplateRow[];
  onSelect: (id: number) => void;
  selected: number | null;
}): ReactNode {
  const canEdit = usePermission("buttons.edit");
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const assign = useMutation({
    mutationFn: ({
      deviceId,
      templateId,
    }: {
      deviceId: number;
      templateId: number | null;
    }) => assignButtonTemplate(deviceId, templateId),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["buttons"] });
      void qc.invalidateQueries({ queryKey: ["prov", "devices"] });
    },
    onError: (err) => setError(errorText(err)),
  });
  const byId = new Map(templates.map((t) => [t.id, t]));

  return (
    <Panel title="Template assignment">
      {devices.length === 0 ? (
        <div className="ui-empty">No devices in the fleet yet.</div>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th>MAC</th>
              <th>Model</th>
              <th>Label</th>
              <th>Template</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => {
              const matching = templates.filter(
                (t) =>
                  t.vendor.toLowerCase() === d.vendor.toLowerCase() &&
                  t.model.toLowerCase() === d.model.toLowerCase(),
              );
              return (
                <tr
                  key={d.id}
                  className={selected === d.id ? "row-selected" : undefined}
                >
                  <td>{d.mac}</td>
                  <td>
                    {d.vendor} {d.model}
                  </td>
                  <td>{d.label ?? "—"}</td>
                  <td>
                    {canEdit ? (
                      <select
                        aria-label={`template for ${d.mac}`}
                        value={d.button_template_id ?? ""}
                        onChange={(e) =>
                          assign.mutate({
                            deviceId: d.id,
                            templateId: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                      >
                        <option value="">— none —</option>
                        {matching.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name} (v{t.version})
                          </option>
                        ))}
                      </select>
                    ) : (
                      (byId.get(d.button_template_id ?? -1)?.name ?? "—")
                    )}
                  </td>
                  <td>
                    <Button variant="secondary" onClick={() => onSelect(d.id)}>
                      Buttons…
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {error ? <Alert>{error}</Alert> : null}
    </Panel>
  );
}

/** An editable device-layer row (string-typed for form state). */
interface DeviceKeyDraft {
  key_index: string;
  source: "device" | "phone_local";
  key_type: string;
  target: string;
  label: string;
  /** The bound line id as a string; "" = follow the phone's active identity. */
  line_id: string;
}

/** "identity 2 · vodialab02 · 1001 (backup)" — how a key's identity is shown. */
function lineLabel(line: DeviceButtonLine): string {
  return `identity ${line.identity} · ${line.node_name} · ${line.extension} (${line.role})`;
}

function DeviceButtonsEditor({ deviceId }: { deviceId: number }): ReactNode {
  const canEdit = usePermission("buttons.edit");
  const qc = useQueryClient();
  const view = useQuery({
    queryKey: ["buttons", "device", deviceId],
    queryFn: () => getDeviceButtons(deviceId),
  });
  const [drafts, setDrafts] = useState<DeviceKeyDraft[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (rows: DeviceButtonRow[]) => setDeviceButtons(deviceId, rows),
    onSuccess: () => {
      setDrafts(null);
      setError(null);
      void qc.invalidateQueries({ queryKey: ["buttons"] });
    },
    onError: (err) => setError(errorText(err)),
  });

  if (view.isLoading) return <Spinner />;
  if (!view.data) return <Alert>Failed to load device buttons.</Alert>;
  const { device, template, device_buttons, effective } = view.data;
  const lines = view.data.lines ?? [];
  const lineById = new Map(lines.map((l) => [l.id, l]));
  const identityText = (
    lineId: number | null,
    role?: string | null,
  ): string => {
    if (lineId !== null) {
      const line = lineById.get(lineId);
      return line
        ? lineLabel(line)
        : `line ${lineId} (no longer on this phone)`;
    }
    const byRole = role ? lines.find((l) => l.role === role) : undefined;
    return byRole
      ? `${lineLabel(byRole)} — by template role`
      : "active identity";
  };

  const startEdit = (): void => {
    setDrafts(
      device_buttons.map((b) => ({
        key_index: String(b.key_index),
        source: b.source,
        key_type: b.key_type ?? "",
        target: b.target ?? "",
        label: b.label ?? "",
        line_id: b.line_id === null ? "" : String(b.line_id),
      })),
    );
  };
  const set = (i: number, patch: Partial<DeviceKeyDraft>): void => {
    if (drafts)
      setDrafts(drafts.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  };
  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!drafts) return;
    save.mutate(
      drafts.map((d) => ({
        key_index: Number(d.key_index),
        source: d.source,
        key_type: d.source === "device" ? d.key_type : null,
        target: d.source === "device" ? d.target || null : null,
        label: d.source === "device" ? d.label || null : null,
        line_id: d.source === "device" && d.line_id ? Number(d.line_id) : null,
      })),
    );
  };

  return (
    <Panel
      title={`Device buttons — ${device.mac} (${device.vendor} ${device.model})`}
    >
      <p>
        Template:{" "}
        {template ? `${template.name} (v${template.version})` : "none"} ·
        Provisioning:{" "}
        <Badge
          tone={device.provisioning_state === "current" ? "online" : "degraded"}
        >
          {device.provisioning_state}
        </Badge>
      </p>

      <h3>Effective layout (what the phone receives)</h3>
      {effective.length === 0 ? (
        <div className="ui-empty">
          No keys render — template empty and no device overrides.
        </div>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th>Key #</th>
              <th>Type</th>
              <th>Target</th>
              <th>Label</th>
              <th>Works through</th>
              <th>From</th>
            </tr>
          </thead>
          <tbody>
            {effective.map((b) => (
              <tr key={b.key_index}>
                <td>{b.key_index}</td>
                <td>{b.key_type ?? "—"}</td>
                <td>{b.target ?? "—"}</td>
                <td>{b.label ?? "—"}</td>
                <td>{identityText(b.line_id, b.line_role)}</td>
                <td>
                  <Badge tone={b.source === "device" ? "degraded" : "neutral"}>
                    {b.source}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Device-layer rows</h3>
      {drafts === null ? (
        <>
          {device_buttons.length === 0 ? (
            <div className="ui-empty">
              No overrides — the template applies unmodified. A{" "}
              <code>phone_local</code> row leaves that key to the phone.
            </div>
          ) : (
            <table className="ui-table">
              <thead>
                <tr>
                  <th>Key #</th>
                  <th>Source</th>
                  <th>Type</th>
                  <th>Target</th>
                  <th>Label</th>
                  <th>Identity</th>
                </tr>
              </thead>
              <tbody>
                {device_buttons.map((b) => (
                  <tr key={b.key_index}>
                    <td>{b.key_index}</td>
                    <td>{b.source}</td>
                    <td>{b.key_type ?? "—"}</td>
                    <td>{b.target ?? "—"}</td>
                    <td>{b.label ?? "—"}</td>
                    <td>
                      {b.source === "device" ? identityText(b.line_id) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {canEdit ? (
            <Button onClick={startEdit}>Edit device buttons</Button>
          ) : null}
        </>
      ) : (
        <form className="editor-form" onSubmit={submit}>
          <table className="ui-table">
            <thead>
              <tr>
                <th>Key #</th>
                <th>Source</th>
                <th>Type</th>
                <th>Target</th>
                <th>Label</th>
                <th>Identity</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((d, i) => (
                <tr key={i}>
                  <td>
                    <input
                      aria-label={`device key ${i} index`}
                      value={d.key_index}
                      size={4}
                      onChange={(e) => set(i, { key_index: e.target.value })}
                    />
                  </td>
                  <td>
                    <select
                      aria-label={`device key ${i} source`}
                      value={d.source}
                      onChange={(e) =>
                        set(i, {
                          source: e.target.value as DeviceKeyDraft["source"],
                        })
                      }
                    >
                      <option value="device">device override</option>
                      <option value="phone_local">
                        phone_local (leave to phone)
                      </option>
                    </select>
                  </td>
                  <td>
                    <input
                      aria-label={`device key ${i} type`}
                      value={d.key_type}
                      size={10}
                      disabled={d.source === "phone_local"}
                      onChange={(e) => set(i, { key_type: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      aria-label={`device key ${i} target`}
                      value={d.target}
                      size={12}
                      disabled={d.source === "phone_local"}
                      onChange={(e) => set(i, { target: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      aria-label={`device key ${i} label`}
                      value={d.label}
                      size={14}
                      disabled={d.source === "phone_local"}
                      onChange={(e) => set(i, { label: e.target.value })}
                    />
                  </td>
                  <td>
                    <select
                      aria-label={`device key ${i} identity`}
                      value={d.line_id}
                      disabled={
                        d.source === "phone_local" || lines.length === 0
                      }
                      onChange={(e) => set(i, { line_id: e.target.value })}
                    >
                      <option value="">active identity</option>
                      {lines.map((l) => (
                        <option key={l.id} value={l.id}>
                          {lineLabel(l)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        setDrafts(drafts.filter((_, j) => j !== i))
                      }
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="ui-hint">
            <strong>Identity</strong> binds a key to one of the phone&apos;s SIP
            identities: a BLF watches the node of the identity it is bound to,
            so pick the other identity for an extension that registers only on
            the other node of the pair. A changed identity reaches the phone in
            seconds but the lamp follows it only after the phone{" "}
            <strong>reboots</strong> (proven on the lab D815, 2026-09-18). Leave
            a key on <em>active identity</em> only on phones with the two pair
            identities.
          </p>
          <div className="pairing-actions">
            <Button
              variant="secondary"
              onClick={() =>
                setDrafts([
                  ...drafts,
                  {
                    key_index: "",
                    source: "device",
                    key_type: "blf",
                    target: "",
                    label: "",
                    line_id: "",
                  },
                ])
              }
            >
              Add row
            </Button>
            <Button type="submit" disabled={save.isPending}>
              Save
            </Button>
            <Button variant="secondary" onClick={() => setDrafts(null)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      {error ? <Alert>{error}</Alert> : null}
    </Panel>
  );
}

/**
 * Import Vodia's button layouts (ADR 0054): a dry run first, then a confirmed
 * commit. Templates become platform templates, per-phone keys the device
 * layer, identities resolve per node; what cannot be carried over is listed.
 */
function VodiaImportPanel(): ReactNode {
  const qc = useQueryClient();
  const canManage = usePermission("buttons.templates.manage");
  const [report, setReport] = useState<ButtonImportReport | null>(null);
  const [sel, setSel] = useState<ImportSelection>(selectAll([], []));
  const [error, setError] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      importVodiaButtons(dryRun, dryRun ? undefined : selectionPayload(sel)),
    onSuccess: (r) => {
      setReport(r);
      setError(null);
      // A fresh dry run ticks everything; the operator unticks what to skip.
      if (r.dry_run) {
        setSel(
          selectAll(
            r.templates.map((t) => t.name),
            r.devices.map((d) => d.mac),
          ),
        );
      }
      if (!r.dry_run) {
        void qc.invalidateQueries({ queryKey: ["buttons"] });
        void qc.invalidateQueries({ queryKey: ["prov", "devices"] });
      }
    },
    onError: (e) => setError(errorText(e)),
  });
  if (!canManage) return null;
  return (
    <Panel title="Import button layouts from Vodia">
      <p className="ui-hint">
        Reads every node's button templates and each discovered phone's own keys (ADR 0054).
        Templates become platform templates named after the Vodia template and node; a
        phone's own keys become its device layer; a numbered identity binds to the phone's
        line on that node. Keys the platform cannot render yet are listed, never invented. A
        phone must be pre-registered first. Re-running replaces imported keys.
      </p>
      <div className="pairing-actions">
        <Button disabled={run.isPending} onClick={() => run.mutate(true)}>
          Dry run
        </Button>
        {report?.dry_run ? (
          <ConfirmButton
            label="Import now"
            question={`Write ${sel.templates.size} of ${report.summary.templates} template(s) and ${sel.devices.size} of ${report.summary.devices} phone layout(s)?`}
            confirmLabel="Yes, import"
            disabled={run.isPending || (sel.templates.size === 0 && sel.devices.size === 0)}
            onConfirm={() => run.mutate(false)}
          />
        ) : null}
      </div>
      {error ? <Alert>{error}</Alert> : null}
      {report ? (
        <>
          <p>
            {report.dry_run ? "Would write" : "Wrote"}: {report.summary.templates} template(s),{" "}
            {report.summary.devices} phone layout(s); {report.summary.findings} finding(s);{" "}
            {report.nodes_read} node(s) read
            {report.applied
              ? ` — ${report.applied.templates_created} created, ${report.applied.templates_updated} updated`
              : ""}
            .
          </p>
          {report.templates.length ? (
            <table className="ui-table">
              <thead>
                <tr>
                  <th>
                    {report.dry_run ? (
                      <input
                        type="checkbox"
                        aria-label="select all templates"
                        checked={sel.templates.size === report.templates.length}
                        onChange={(e) =>
                          setSel(
                            setAll(
                              sel,
                              "templates",
                              report.templates.map((t) => t.name),
                              e.target.checked,
                            ),
                          )
                        }
                      />
                    ) : null}
                  </th>
                  <th>Template</th>
                  <th>Model</th>
                  <th>Keys</th>
                  <th>Skipped</th>
                </tr>
              </thead>
              <tbody>
                {report.templates.map((t) => (
                  <tr key={t.name}>
                    <td>
                      {report.dry_run ? (
                        <input
                          type="checkbox"
                          aria-label={`import template ${t.name}`}
                          checked={sel.templates.has(t.name)}
                          onChange={() => setSel(toggle(sel, "templates", t.name))}
                        />
                      ) : null}
                    </td>
                    <td>{t.name}</td>
                    <td>
                      {t.vendor} {t.model}
                      {t.sidecar_count ? ` + ${t.sidecar_count} sidecar` : ""}
                    </td>
                    <td>{t.keys}</td>
                    <td>{t.skipped}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {report.devices.length ? (
            <table className="ui-table">
              <thead>
                <tr>
                  <th>
                    {report.dry_run ? (
                      <input
                        type="checkbox"
                        aria-label="select all phones"
                        checked={sel.devices.size === report.devices.length}
                        onChange={(e) =>
                          setSel(
                            setAll(
                              sel,
                              "devices",
                              report.devices.map((d) => d.mac),
                              e.target.checked,
                            ),
                          )
                        }
                      />
                    ) : null}
                  </th>
                  <th>Phone</th>
                  <th>Node / domain</th>
                  <th>Template</th>
                  <th>Own keys</th>
                  <th>Phone-local keys</th>
                </tr>
              </thead>
              <tbody>
                {report.devices.map((d) => (
                  <tr key={d.mac}>
                    <td>
                      {report.dry_run ? (
                        <input
                          type="checkbox"
                          aria-label={`import phone ${d.mac}`}
                          checked={sel.devices.has(d.mac)}
                          onChange={() => setSel(toggle(sel, "devices", d.mac))}
                        />
                      ) : null}
                    </td>
                    <td>{d.mac}</td>
                    <td>
                      {d.node} / {d.domain}
                    </td>
                    <td>{d.template ?? "—"}</td>
                    <td>{d.device_keys}</td>
                    <td>{d.phone_local_keys}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {report.findings.length ? (
            <table className="ui-table">
              <thead>
                <tr>
                  <th>Finding</th>
                  <th>Where</th>
                  <th>Key</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {report.findings.map((f, i) => (
                  <tr key={i}>
                    <td>
                      <Badge tone="degraded">{f.kind.replaceAll("_", " ")}</Badge>
                    </td>
                    <td>
                      {f.node}
                      {f.domain ? ` / ${f.domain}` : ""} · {f.subject}
                    </td>
                    <td>{f.key ?? "—"}</td>
                    <td>{f.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </>
      ) : null}
    </Panel>
  );
}

export function ButtonsPage(): ReactNode {
  const devices = useQuery({
    queryKey: ["prov", "devices"],
    queryFn: getDevices,
  });
  const templates = useQuery({
    queryKey: ["buttons", "templates"],
    queryFn: getButtonTemplates,
  });
  const [selected, setSelected] = useState<number | null>(null);

  return (
    <div>
      <div className="dash__head">
        <h1>Buttons</h1>
      </div>
      <VodiaImportPanel />
      <TemplateLibrary />
      <AssignmentTable
        devices={devices.data?.items ?? []}
        templates={templates.data?.items ?? []}
        onSelect={setSelected}
        selected={selected}
      />
      {selected !== null ? <DeviceButtonsEditor deviceId={selected} /> : null}
    </div>
  );
}
